import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tanstack/react-form";
import * as Headless from "@headlessui/react";
import { ErrorMessage, Label, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import Form from "src/components/base/form";
import { toast } from "react-toastify";
import { Listbox, ListboxLabel, ListboxOption } from "src/components/base/listbox";
import { Button } from "src/components/base/button";
import { Combobox, ComboboxOption } from "src/components/base/combobox";
import { Text } from "src/components/base/text";
import { Dialog, DialogActions, DialogDescription, DialogTitle } from "src/components/base/dialog";

import { Badge } from "src/components/base/badges/base";

import clsx from "clsx";
import { LockClosedIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/20/solid";
import ACCESS_RULE_CONTEXT from "src/context/internal/access-rule";
import {
    AccessRuleProtocol,
    FullAccessRule,
    FullAccessRuleDst,
    FullAccessRuleSrc,
} from "src/api/generated/frontend_internal_v1";
import { ApiInternal } from "src/api/api_internal";
import { TagBadge } from "src/components/base/badges/tag";
import { SubnetBadge } from "src/components/base/badges/subnet";
import { GroupBadge } from "src/components/base/badges/group";

/**
 * The properties for {@link ACL}
 */
export type ACLProps = {
    /** Callback for changes */
    onChange: () => void;

    /** An existing ACL to display */
    acl?: FullAccessRule;

    /** Start the ACL editable */
    startEditable?: boolean;
} & (
    | {
          /** The source of the ACL */
          src: FullAccessRuleSrc;
          /** Only one of src, dst can be set */
          dst?: never;
      }
    | {
          /** Only one of src, dst can be set */
          src?: never;
          /** The destination of the ACL */
          dst: FullAccessRuleDst;
      }
);

/**
 * Form for editing an ACL
 */
export function ACL(props: ACLProps) {
    const [t] = useTranslation("component-acl");
    const [tg] = useTranslation();

    const accessRuleCtx = React.useContext(ACCESS_RULE_CONTEXT);

    const [search, setSearch] = React.useState("");

    const [openDelete, setOpenDelete] = React.useState(false);
    const [editable, setEditable] = React.useState(props.startEditable ?? false);

    const locked =
        (props.acl?.src?.type === "Tag" && props.acl.src?.value.locked) ||
        (props.acl?.dst?.type === "Tag" && props.acl.dst?.value.locked) ||
        false;

    const form = useForm({
        defaultValues: {
            port: props.acl
                ? ["Tcp", "Udp", "Sctp"].includes(props.acl.protocol)
                    ? props.acl.port.toString()
                    : ""
                : "",
            port_range:
                props.acl && props.acl.port_range_end
                    ? ["Tcp", "Udp", "Sctp"].includes(props.acl.protocol)
                        ? props.acl.port_range_end.toString()
                        : ""
                    : "",
            protocol: props.acl ? props.acl.protocol : ("Tcp" as AccessRuleProtocol),
            description: props.acl ? props.acl.description : "",

            src: props.acl ? props.acl.src : (props.src ?? null),
            dst: props.acl ? props.acl.dst : (props.dst ?? null),
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (value.dst === null) {
                    return {
                        fields: {
                            dst: t("error.field-is-required"),
                        },
                    };
                }

                if (value.src === null) {
                    return {
                        fields: {
                            src: t("error.field-is-required"),
                        },
                    };
                }

                // If src and dst is the same subnet, block creation / update as we can't apply any rules to the distant subnet
                if (
                    value.src.type === "Subnet" &&
                    value.dst.type === "Subnet" &&
                    value.src.value.uuid === value.dst.value.uuid
                ) {
                    return {
                        fields: {
                            dst: props.src ? t("error.cannot-apply-rules-on-same-subnet") : undefined,
                            src: props.dst ? t("error.cannot-apply-rules-on-same-subnet") : undefined,
                        },
                    };
                }

                if (props.acl) {
                    // Update existing ACL
                    await ApiInternal.accessRules.update(props.acl.uuid, {
                        src: !props.src ? { ...value.src, value: value.src.value.uuid } : undefined, // If we have a src, we don't support updating it
                        dst: !props.dst ? { ...value.dst, value: value.dst.value.uuid } : undefined, // If we have a dst, we don't support updating it
                        protocol: value.protocol,
                        port: ["Tcp", "Udp", "Sctp"].includes(value.protocol)
                            ? value.port_range !== ""
                                ? { type: "Range", start: parseInt(value.port), end: parseInt(value.port_range) }
                                : {
                                      type: "Single",
                                      port: parseInt(value.port),
                                  }
                            : { type: "Any" },
                        description: value.description,
                    });

                    toast.success(t("toast.acl-updated"));
                    setEditable(false);
                } else {
                    // New ACL
                    await ApiInternal.accessRules.create({
                        src: { ...value.src, value: value.src.value.uuid },
                        dst: { ...value.dst, value: value.dst.value.uuid },
                        protocol: value.protocol,
                        port: ["Tcp", "Udp", "Sctp"].includes(value.protocol)
                            ? value.port_range !== ""
                                ? { type: "Range", start: parseInt(value.port), end: parseInt(value.port_range) }
                                : {
                                      type: "Single",
                                      port: parseInt(value.port),
                                  }
                            : { type: "Any" },
                        description: value.description,
                    });

                    toast.success(t("toast.acl-created"));
                    form.reset();
                }
                props.onChange();
            },
        },
    });

    /**
     * Delete the given ACL
     */
    const deleteAcl = async () => {
        if (!props.acl) {
            return;
        }
        await ApiInternal.accessRules.delete(props.acl.uuid);
        props.onChange();
    };

    const filteredSrcOrDst =
        search === ""
            ? {
                  tags: accessRuleCtx.options.tags.filter((tag) => !tag.locked),
                  groups: accessRuleCtx.options.groups,
                  subnets: accessRuleCtx.options.subnets,
              }
            : {
                  tags: accessRuleCtx.options.tags
                      .filter((tag) => !tag.locked)
                      .filter((tag) => tag.name.toLowerCase().includes(search.toLowerCase())),
                  groups: accessRuleCtx.options.groups.filter((group) =>
                      group.name.toLowerCase().includes(search.toLowerCase()),
                  ),
                  subnets: accessRuleCtx.options.subnets.filter((subnet) =>
                      subnet.name.toLowerCase().includes(search.toLowerCase()),
                  ),
              };

    // Return non-editable form
    if (!editable) {
        return (
            <div
                className={clsx(
                    "grid grid-cols-[1fr_8em] gap-6 rounded-lg border px-4 py-2 lg:flex-row lg:gap-12",
                    "bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50",
                )}
            >
                <div className={"grid w-full grid-cols-3 gap-x-3 gap-y-1"}>
                    {props.dst && (
                        <>
                            <Text className={"order-1"}>{t("label.source")}</Text>
                            <form.Field name={"src"}>
                                {(fieldApi) =>
                                    fieldApi.state.value === null ? (
                                        ""
                                    ) : fieldApi.state.value.type === "Tag" ? (
                                        <TagBadge className={"order-4 w-fit"} tag={fieldApi.state.value.value} />
                                    ) : fieldApi.state.value.type === "Group" ? (
                                        <GroupBadge className={"order-4 w-fit"} group={fieldApi.state.value.value} />
                                    ) : (
                                        <SubnetBadge
                                            className={"order-4 w-fit"}
                                            subnet={fieldApi.state.value.value}
                                        ></SubnetBadge>
                                    )
                                }
                            </form.Field>
                        </>
                    )}
                    {props.src && (
                        <>
                            <Text className={"order-1"}>{t("label.target")}</Text>
                            <form.Field name={"dst"}>
                                {(fieldApi) =>
                                    fieldApi.state.value === null ? (
                                        ""
                                    ) : fieldApi.state.value.type === "Tag" ? (
                                        <TagBadge className={"order-4 w-fit"} tag={fieldApi.state.value.value} />
                                    ) : fieldApi.state.value.type === "Group" ? (
                                        <GroupBadge className={"order-4 w-fit"} group={fieldApi.state.value.value} />
                                    ) : (
                                        <SubnetBadge
                                            className={"order-4 w-fit"}
                                            subnet={fieldApi.state.value.value}
                                        ></SubnetBadge>
                                    )
                                }
                            </form.Field>
                        </>
                    )}

                    <Text className={"order-2"}>{t("label.protocol")}</Text>
                    <form.Field name={"protocol"}>
                        {(fieldApi) => <Text className={"order-5"}>{fieldApi.state.value.toUpperCase()}</Text>}
                    </form.Field>

                    <Text className={"order-3"}>{t("label.port")}</Text>
                    <form.Field name={"port"}>
                        {(fieldApi) => (
                            <Text className={"order-6"}>
                                {form.state.values.port_range
                                    ? `${fieldApi.state.value} - ${form.state.values.port_range}`
                                    : fieldApi.state.value}
                            </Text>
                        )}
                    </form.Field>

                    <form.Field name={"description"}>
                        {(fieldApi) => <Text className={"order-7 col-span-3"}>{fieldApi.state.value}</Text>}
                    </form.Field>
                </div>

                {locked ? (
                    <Badge className={"h-fit w-fit place-self-end self-center"} color={"orange"}>
                        <LockClosedIcon className={"size-4"} />
                        <span>{t("label.read-only")}</span>
                    </Badge>
                ) : (
                    <Button
                        className={"place-self-end self-center"}
                        aria-label={"edit"}
                        plain={true}
                        onClick={() => setEditable(true)}
                    >
                        <PencilSquareIcon />
                    </Button>
                )}
            </div>
        );
    }

    // Return editable form
    return (
        <Form onSubmit={form.handleSubmit}>
            <div
                className={clsx(
                    "flex flex-col justify-between gap-6 rounded-lg border p-2 lg:flex-row lg:gap-12",
                    "bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50",
                )}
            >
                <Headless.Fieldset
                    className={
                        "grid w-full grid-cols-[1fr_auto_1fr] gap-x-6 gap-y-2 px-6 py-2 lg:grid-cols-[2fr_1fr_2fr_auto_2fr]"
                    }
                >
                    {props.dst && (
                        <form.Field name={"src"}>
                            {(fieldApi) => (
                                <>
                                    <RequiredLabel className={"order-1 max-lg:col-span-2"}>
                                        {t("label.source")}
                                    </RequiredLabel>
                                    <Combobox
                                        className={"order-3 max-lg:col-span-2 lg:order-6"}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e)}
                                        onClose={() => setSearch("")}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                        queryDisplay={(q) =>
                                            q
                                                ? `${q.type === "Tag" ? "tag" : q.type === "Group" ? "group" : "subnet"}:${"name" in q.value ? q.value["name"] : ""}`
                                                : ""
                                        }
                                        onQueryChange={(q) => setSearch(q)}
                                    >
                                        <>
                                            {filteredSrcOrDst.tags.map((tag) => (
                                                <ComboboxOption value={{ type: "Tag", value: tag }} key={tag.uuid}>
                                                    <Label>{`tag:${tag.name}`}</Label>
                                                </ComboboxOption>
                                            ))}
                                            {filteredSrcOrDst.groups.map((group) => (
                                                <ComboboxOption
                                                    value={{ type: "Group", value: group }}
                                                    key={group.uuid}
                                                >
                                                    <Label>{`group:${group.name}`}</Label>
                                                </ComboboxOption>
                                            ))}
                                            {filteredSrcOrDst.subnets.map((subnet) => (
                                                <ComboboxOption
                                                    value={{ type: "Subnet", value: subnet }}
                                                    key={subnet.uuid}
                                                >
                                                    <Label>{`subnet:${subnet.name}`}</Label>
                                                </ComboboxOption>
                                            ))}
                                        </>
                                    </Combobox>
                                    <Headless.Field className={"order-5 max-lg:col-span-2 lg:order-11"}>
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Headless.Field>
                                </>
                            )}
                        </form.Field>
                    )}
                    {props.src && (
                        <form.Field name={"dst"}>
                            {(fieldApi) => (
                                <>
                                    <RequiredLabel className={"order-1 max-lg:col-span-2"}>
                                        {t("label.target")}
                                    </RequiredLabel>
                                    <Combobox
                                        className={"order-3 max-lg:col-span-2 lg:order-6"}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e)}
                                        onClose={() => setSearch("")}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                        queryDisplay={(q) =>
                                            q
                                                ? `${q.type === "Tag" ? "tag" : q.type === "Group" ? "group" : "subnet"}:${"name" in q.value ? q.value["name"] : ""}`
                                                : ""
                                        }
                                        onQueryChange={(q) => setSearch(q)}
                                    >
                                        <>
                                            {filteredSrcOrDst.tags.map((tag) => (
                                                <ComboboxOption value={{ type: "Tag", value: tag }} key={tag.uuid}>
                                                    <Label>{`tag:${tag.name}`}</Label>
                                                </ComboboxOption>
                                            ))}
                                            {filteredSrcOrDst.groups.map((group) => (
                                                <ComboboxOption
                                                    value={{ type: "Group", value: group }}
                                                    key={group.uuid}
                                                >
                                                    <Label>{`group:${group.name}`}</Label>
                                                </ComboboxOption>
                                            ))}
                                            {filteredSrcOrDst.subnets.map((subnet) => (
                                                <ComboboxOption
                                                    value={{ type: "Subnet", value: subnet }}
                                                    key={subnet.uuid}
                                                >
                                                    <Label>{`subnet:${subnet.name}`}</Label>
                                                </ComboboxOption>
                                            ))}
                                        </>
                                    </Combobox>
                                    <Headless.Field className={"order-5 max-lg:col-span-2 lg:order-11"}>
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Headless.Field>
                                </>
                            )}
                        </form.Field>
                    )}

                    <form.Field name={"protocol"}>
                        {(fieldApi) => (
                            <>
                                <RequiredLabel className={"order-2"}>{t("label.protocol")}</RequiredLabel>
                                <Listbox
                                    className={"order-4 h-fit lg:order-7"}
                                    value={fieldApi.state.value}
                                    onChange={(proto) => fieldApi.handleChange(proto)}
                                >
                                    {Object.keys(AccessRuleProtocol).map((key) => (
                                        <ListboxOption value={key} key={key}>
                                            <ListboxLabel>{key.toUpperCase()}</ListboxLabel>
                                        </ListboxOption>
                                    ))}
                                </Listbox>
                                <div className={"order-6 lg:order-12"}></div>
                            </>
                        )}
                    </form.Field>

                    <form.Subscribe>
                        {(state) => (
                            <form.Field
                                name={"port"}
                                validators={{
                                    onChangeListenTo: ["port", "protocol"],
                                    onChange: ({ fieldApi, value }) => {
                                        if (
                                            (parseInt(value) <= 0 || parseInt(value) >= 65536) &&
                                            ["Tcp", "Udp", "Sctp"].includes(fieldApi.form.getFieldValue("protocol"))
                                        ) {
                                            return t("error.port-not-in-range");
                                        }

                                        return undefined;
                                    },
                                }}
                            >
                                {(fieldApi) => (
                                    <>
                                        <RequiredLabel className={"order-7 lg:order-3"}>
                                            {t("label.port")}
                                        </RequiredLabel>
                                        <Input
                                            className={"order-10 h-fit max-lg:!mt-0 lg:order-8"}
                                            type={"number"}
                                            required={true}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            disabled={!["Tcp", "Udp", "Sctp"].includes(state.values.protocol)}
                                        />
                                        <div className={"order-13 lg:order-13"}>
                                            {fieldApi.state.meta.errors.map((err) => (
                                                <Text className={"!text-red-500"}>{err}</Text>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </form.Field>
                        )}
                    </form.Subscribe>
                    <span className={"order-8 lg:order-4"}></span>
                    <Text className={"order-11 flex items-center lg:order-9"}>-</Text>
                    <div className={"order-14"}></div>
                    <form.Subscribe>
                        {(state) => (
                            <form.Field
                                name={"port_range"}
                                validators={{
                                    onChangeListenTo: ["port", "protocol"],
                                    onChange: ({ fieldApi, value }) => {
                                        if (
                                            parseInt(value) <= parseInt(fieldApi.form.getFieldValue("port")) &&
                                            ["Tcp", "Udp", "Sctp"].includes(fieldApi.form.getFieldValue("protocol"))
                                        ) {
                                            return t("error.port-end-too-small");
                                        }
                                        if (
                                            (parseInt(value) <= 0 || parseInt(value) >= 65536) &&
                                            ["Tcp", "Udp", "Sctp"].includes(fieldApi.form.getFieldValue("protocol"))
                                        ) {
                                            return t("error.port-not-in-range");
                                        }

                                        return undefined;
                                    },
                                }}
                            >
                                {(fieldApi) => (
                                    <>
                                        <Label className={"order-9 lg:order-5"}>{t("label.port-range")}</Label>
                                        <Input
                                            className={"order-12 h-fit max-lg:!mt-0 lg:order-10"}
                                            type={"number"}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            disabled={!["Tcp", "Udp", "Sctp"].includes(state.values.protocol)}
                                        />
                                        <div className={"order-15"}>
                                            {fieldApi.state.meta.errors.map((err) => (
                                                <Text className={"!text-red-500"}>{err}</Text>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </form.Field>
                        )}
                    </form.Subscribe>

                    <form.Field name={"description"}>
                        {(fieldApi) => (
                            <Headless.Field className={"order-16 col-span-3 lg:col-span-5"}>
                                <Label>{t("label.description")}</Label>
                                <Input
                                    className={"mt-2 w-full max-w-lg"}
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                />
                            </Headless.Field>
                        )}
                    </form.Field>
                </Headless.Fieldset>
                <div className={"flex flex-row-reverse justify-start lg:flex-col lg:justify-between"}>
                    {props.acl ? (
                        <Button plain={true} className={"self-end"} onClick={() => setOpenDelete(true)}>
                            <TrashIcon />
                        </Button>
                    ) : (
                        <span></span>
                    )}

                    <div className={"flex gap-2"}>
                        {props.acl && (
                            <Button plain={true} onClick={() => setEditable(false)}>
                                {tg("button.cancel")}
                            </Button>
                        )}
                        <Button className={"self-end"} outline={true} type={"submit"}>
                            {tg("button.save")}
                        </Button>
                    </div>
                </div>
            </div>

            {openDelete && (
                <Suspense>
                    <Dialog open={true} onClose={() => setOpenDelete(false)}>
                        <DialogTitle>{t("heading.delete-acl")}</DialogTitle>
                        <DialogDescription>{t("description.delete-acl")}</DialogDescription>
                        <DialogActions>
                            <Button onClick={() => setOpenDelete(false)}>{tg("button.cancel")}</Button>
                            <Button
                                color={"red"}
                                onClick={async () => {
                                    await deleteAcl();
                                    setOpenDelete(false);
                                }}
                            >
                                {t("button.delete-acl")}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Suspense>
            )}
        </Form>
    );
}

/**
 * The properties for {@link ReadOnlyACLs}
 */
export type ReadOnlyACLsProps = {
    /** The ACL that should be displayed */
    rules: Array<FullAccessRule>;
} & (
    | {
          /** The source of the ACL */
          src?: FullAccessRuleSrc;
          /** Only one of src, dst can be set */
          dst?: never;
      }
    | {
          /** Only one of src, dst can be set */
          src?: never;
          /** The destination of the ACL */
          dst?: FullAccessRuleDst;
      }
);

/**
 * A view for a read only ACL
 */
export default function ReadOnlyACLs(props: ReadOnlyACLsProps) {
    const [t] = useTranslation("component-acl");

    const showDstOrSrc = props.src !== undefined || props.dst !== undefined;

    return (
        <div className="inline-block min-w-full text-left align-middle text-sm/6">
            <div
                className={clsx(
                    "grid border-b border-b-zinc-950/10 px-4 py-2 text-zinc-500 dark:border-b-white/10 dark:text-zinc-400",
                    showDstOrSrc
                        ? "grid-cols-[1fr_1fr_1fr] sm:grid-cols-[1fr_1fr_1fr_3fr]"
                        : "grid-cols-[1fr_1fr] sm:grid-cols-[1fr_1fr_4fr]",
                )}
            >
                {showDstOrSrc && props.dst && <div className="font-medium">{t("label.source")}</div>}
                {showDstOrSrc && props.src && <div className="font-medium">{t("label.target")}</div>}
                <div className="font-medium">{t("label.protocol")}</div>
                <div className="font-medium">{t("label.port")}</div>
                <div className="hidden font-medium sm:table-cell">{t("label.description")}</div>
            </div>
            {props.rules.map((rule, index) => (
                <div
                    key={rule.uuid}
                    className={clsx(
                        "grid px-4 py-2 text-zinc-700 dark:text-zinc-200",
                        index % 2 === 0 ? "even:bg-zinc-950/[2.5%] dark:even:bg-white/[2.5%]" : "",
                        showDstOrSrc
                            ? "grid-cols-[1fr_1fr_1fr] sm:grid-cols-[1fr_1fr_1fr_3fr]"
                            : "grid-cols-[1fr_1fr] sm:grid-cols-[1fr_1fr_4fr]",
                    )}
                >
                    {showDstOrSrc && props.dst && (
                        <div className="font-medium">
                            {rule.src.type === "Tag" ? (
                                <TagBadge tag={rule.src.value} />
                            ) : rule.src.type === "Group" ? (
                                <GroupBadge group={rule.src.value} />
                            ) : (
                                <SubnetBadge subnet={rule.src.value} />
                            )}
                        </div>
                    )}
                    {showDstOrSrc && props.src && (
                        <div className="font-medium">
                            {rule.dst.type === "Tag" ? (
                                <TagBadge tag={rule.dst.value} />
                            ) : rule.dst.type === "Group" ? (
                                <GroupBadge group={rule.dst.value} />
                            ) : (
                                <SubnetBadge subnet={rule.dst.value} />
                            )}
                        </div>
                    )}
                    <span className="relative">{rule.protocol.toUpperCase()}</span>
                    <span className="relative">
                        {["Tcp", "Udp", "Sctp"].includes(rule.protocol)
                            ? rule.port_range_end
                                ? `${rule.port} - ${rule.port_range_end}`
                                : rule.port
                            : ""}
                    </span>
                    <span className="relative col-span-3 text-left break-words whitespace-pre-wrap sm:col-auto">
                        {rule.description}
                    </span>
                </div>
            ))}
        </div>
    );
}
